// @flow

import MainApi from "./MainApi"
import qs from "qs"

export type VehicleIdentifiers = Array<{ countryCode: string, regNo: string }>

export const BASE_URL = "fleet-api/api"
const searchRequest = MainApi.cancableGetRequest()

export type ApproveSignupProps = {
  signupEntryId?: string,
  fleetId?: string,
  transporterNumber?: string | number,
  shouldApprove?: boolean,
  licenseId?: string | number
}

export default {
  /*
  Products
  */
  products: {
    getAllProducts: async (): Promise<any> => {
      const {
        data: { productsConfiguration }
      } = await MainApi.get(`${BASE_URL}/products/config`)

      return productsConfiguration
    },

    getProduct: async (id: string): Promise<any> => {
      const { data: product } = await MainApi.get(
        `${BASE_URL}/products/config/${id}`
      )
      return product
    },

    getProductOptions: async (id: string): Promise<any> => {
      const {
        data: { options }
      } = await MainApi.get(`${BASE_URL}/products/config/${id}`)

      return options
    },

    createProduct: async (product: Object): Promise<any> => {
      const {
        data: { id }
      } = await MainApi.post(`${BASE_URL}/products/config`, { ...product })
      return id
    },

    updateProduct: async (productId: string, product: Object): Promise<any> => {
      const {
        data: { id }
      } = await MainApi.put(`${BASE_URL}/products/config/${productId}`, {
        ...product
      })
      return id
    },

    getAvailableProductTypes: async (): Promise<any> => {
      const { data: productTypes } = await MainApi.get(
        `${BASE_URL}/attributes/config/productTypes`
      )
      return productTypes
    }
  },

  /*
  AttributeIdMapping
   */

  attributeIdMapping: {
    getMapping: async ({
      attributeId
    }: {
      attributeId: string
    }): Promise<empty | null> => {
      try {
        const { data: trafficMapping } = await MainApi.get(
          `${BASE_URL}/attribute/idmapping/${attributeId}`
        )
        return trafficMapping
      } catch (e) {
        // request return a 404 if no mappings exist for that attributeId
        if (e.response.status === 404) return null
        throw e
      }
    },
    updateMapping: async ({
      attributeId,
      trafficMapping
    }: {
      attributeId: string,
      trafficMapping: Object
    }) => {
      await MainApi.put(
        `${BASE_URL}/attribute/idmapping/${attributeId}`,
        trafficMapping
      )
    }
  },

  /*
  Attributes
   */
  attributes: {
    getAllAttributes: async (): Promise<any> => {
      const {
        data: { attributesConfig }
      } = await MainApi.get(`${BASE_URL}/attributes/config`)
      return attributesConfig
    },

    getAttribute: async (id: string): Promise<any> => {
      const { data: attribute } = await MainApi.get(
        `${BASE_URL}/attributes/config/${id}`
      )
      return attribute
    },

    createAttribute: async (attribute: Object): Promise<any> => {
      const {
        data: { id }
      } = await MainApi.post(`${BASE_URL}/attributes/config`, {
        ...attribute
      })
      return id
    },

    updateAttribute: async (
      attributeId: string,
      attribute: Object
    ): Promise<any> => {
      const {
        data: { id }
      } = await MainApi.put(`${BASE_URL}/attributes/config/${attributeId}`, {
        ...attribute
      })
      return id
    },

    getAvailableAttributeTypes: async (): Promise<any> => {
      const { data: attributeTypes } = await MainApi.get(
        `${BASE_URL}/attributes/config/attributeTypes`
      )
      return attributeTypes
    }
  },
  /*
  Vehicles
   */

  vehicles: {
    getAllVehicles: async (query: string): Promise<any> => {
      const { data } = await MainApi.get(`${BASE_URL}/vehicles?${query}`)
      return data.vehicles
    },

    getVehicle: async (id: string): Promise<any> => {
      const { data } = await MainApi.get(`${BASE_URL}/vehicles/${id}`)
      return data
    },

    getLiveVehicles: async (fleets: Array<string>): Promise<any> => {
      const { data } = await searchRequest(
        `${BASE_URL}/vehicles/live?fleetIds=${fleets.join(",")}`
      )
      return data
    },

    createVehicle: async (data: Object): Promise<any> => {
      const response = await MainApi.post(`${BASE_URL}/vehicles`, data)
      return response
    },

    /**
     * Import multiple vehicles
     */
    createManyVehicles: async (
      vehicleIdentifiers: VehicleIdentifiers
    ): Promise<any> => {
      const response = await MainApi.post(`${BASE_URL}/vehicles/vehicles`, {
        vehicleIdentifiers
      })
      return response
    },

    updateVehicle: async (vehicleId: string, data: Object): Promise<any> => {
      const response = await MainApi.patch(
        `${BASE_URL}/vehicles/${vehicleId}`,
        data
      )
      return response
    },

    deleteVehicle: async (vehicleId: string): Promise<any> => {
      const response = await MainApi.delete(`${BASE_URL}/vehicles/${vehicleId}`)
      return response
    },

    addNotice: async (id: string, data: Object): Promise<any> => {
      const response = await MainApi.post(
        `${BASE_URL}/vehicles/${id}/notices`,
        data
      )
      return response
    },

    updateTaximeter: async (vehicleId: string, data: Object): Promise<any> => {
      const response = await MainApi.patch(
        `${BASE_URL}/vehicles/${vehicleId}/taximeter`,
        data
      )
      return response
    },

    searchRegNrTxmNr: async (
      regNrOrTxmNr: string,
      countryCode: string = "SE"
    ): Promise<any> => {
      const { data } = await searchRequest(
        `${BASE_URL}/vehicles/search?query=${regNrOrTxmNr}&countryCode=${countryCode}`
      )
      return data
    },

    getOfficialRecord: async ({
      regNr,
      countryCode
    }: {
      regNr: string,
      countryCode: string
    }): Promise<any> => {
      const { data } = await searchRequest(
        `${BASE_URL}/vehicles/${countryCode}/${regNr}/official-record`
      )
      return data
    },

    getAuditLogs: async (id: string): Promise<any> => {
      const { data } = await MainApi.get(`${BASE_URL}/vehicles/${id}/logs`)
      return data
    },

    activateRC: async (vehicleId: string): Promise<any> => {
      const {
        data: { id }
      } = await MainApi.post(`${BASE_URL}/vehicles/${vehicleId}/rc`)
      return id
    },
    fetchOfficialReport: async (): Promise<any> => {
      const { data } = await MainApi.get(
        `${BASE_URL}/vehicles/report/stream/excel`,
        {
          responseType: "arraybuffer"
        }
      )
      return data
    },
    fetchInactiveVehiclesReport: async (): Promise<any> => {
      const {
        data
      } = await MainApi.get(
        `${BASE_URL}/vehicles/report/stream/excel/inactive`,
        { responseType: "arraybuffer" }
      )
      return data
    },
    uploadReportWithFossilFree: async ({
      data
    }: {
      data: Object
    }): Promise<any> => {
      const response = await MainApi.post(
        `${BASE_URL}/vehicles/report/upload/doc/fossil-free`,
        data
      )
      return response
    },
    updateOfficialRecord: async ({
      regNr,
      countryCode
    }: {
      regNr: string,
      countryCode: string
    }): Promise<any> => {
      const { data } = await MainApi.post(
        `${BASE_URL}/vehicles/${countryCode}/${regNr}/official-record`
      )
      return data
    }
  },
  /*
  Brands
   */
  brands: {
    getFleetBrands: async (): Promise<any> => {
      const response = await MainApi.get(`${BASE_URL}/brands`)
      return response
    }
  },

  /*
  Fleets
   */
  fleets: {
    getAllFleets: async (): Promise<any> => {
      const response = await MainApi.get(`${BASE_URL}/fleets`)
      return response
    },

    getFleet: async (id: string): Promise<any> => {
      const { data: fleet } = await MainApi.get(`${BASE_URL}/fleets/${id}`)
      return fleet
    },

    createFleet: async (values: Object): Promise<any> => {
      const { data } = await MainApi.post(`${BASE_URL}/fleets`, values)
      return data
    },

    updateFleet: async (fleetId: string, data: Object): Promise<any> => {
      const {
        data: { id }
      } = await MainApi.put(`${BASE_URL}/fleets/${fleetId}`, data)
      return id
    },

    addFleetUser: async (id: string, values: Object): Promise<any> => {
      const { data } = await MainApi.post(
        `${BASE_URL}/fleets/${id}/users`,
        values
      )
      return data
    },

    deleteFleetUser: async (fleetId: string, userId: number) => {
      await MainApi.delete(`${BASE_URL}/fleets/${fleetId}/users/${userId}`)
    },

    addFleetService: async (id: string, serviceType: string, data: Object) => {
      await MainApi.put(
        `${BASE_URL}/fleets/${id}/application/${serviceType}`,
        data
      )
    },

    deleteFleetService: async (id: string, serviceType: string) => {
      await MainApi.delete(
        `${BASE_URL}/fleets/${id}/application/${serviceType}`
      )
    },

    getProductsConfiguration: async (
      id: string
    ): Promise<{
      optionsSettings: empty,
      ruleBasedOptions: empty,
      subProductsConfigurations: empty,
      groupId: string
    }> => {
      const {
        data: {
          optionsSettings,
          productConfigurations: subProductsConfigurations,
          ruleBasedOptions,
          groupId
        }
      } = await MainApi.get(`${BASE_URL}/fleets/${id}/config`)

      return {
        optionsSettings,
        subProductsConfigurations,
        ruleBasedOptions,
        groupId
      }
    },

    getFleetOptions: async (): Promise<any> => {
      const { data: options } = await MainApi.get(
        `${BASE_URL}/fleets/field-options`
      )
      return options
    },

    updateFleetOptions: async (
      fleetId: string,
      attributeId: number,
      options: Object
    ) => {
      await MainApi.put(
        `${BASE_URL}/fleets/${fleetId}/config/options/${attributeId}`,
        options
      )
    },

    updateFleetProduct: async (
      fleetId: string,
      productId: string,
      data: Object
    ) => {
      await MainApi.put(
        `${BASE_URL}/fleets/${fleetId}/config/product/${productId}`,
        data
      )
    },

    updateFleetPreBookSettings: async (fleetId: string, values: Object) => {
      await MainApi.put(
        `${BASE_URL}/fleets/${fleetId}/config/rules/preBookSettings`,
        values
      )
    },

    deleteFleetPreBookSettings: async (fleetId: string) => {
      await MainApi.delete(
        `${BASE_URL}/fleets/${fleetId}/config/rules/preBookSettings`
      )
    },

    updateFleetContact: async (id: string, data: Object) => {
      await MainApi.put(`${BASE_URL}/fleets/${id}/contact`, data)
    },

    updateFleetCoverage: async (id: string, data: Object) => {
      await MainApi.put(`${BASE_URL}/fleets/${id}/coverage`, data)
    },

    updateFleetBdSystem: async (id: string, data: Object): Promise<any> => {
      const res = await MainApi.put(`${BASE_URL}/fleets/${id}/bd-system`, data)
      return res
    }
  },
  /*
  Transporters
   */
  transporters: {
    getTransporters: async (fleetId?: string): Promise<any> => {
      let url = `${BASE_URL}/transporters`
      if (fleetId) {
        url += `/fleet/${fleetId}`
      }
      const { data } = await MainApi.get(url)
      return data
    },

    getTransporter: async (id: string): Promise<any> => {
      const response = await MainApi.get(`${BASE_URL}/transporters/${id}`)
      return response
    },

    createTransporter: async (values: Object): Promise<any> => {
      const response = await MainApi.post(`${BASE_URL}/transporters`, values)
      return response
    },

    updateTransporter: async (id: string, values: Object) => {
      await MainApi.patch(`${BASE_URL}/transporters/${id}`, values)
    },

    addTransporterUser: async (id: string, data: Object): Promise<any> => {
      const response = await MainApi.post(
        `${BASE_URL}/transporters/${id}/users`,
        data
      )
      return response
    },

    deleteTransporterUser: async (transporterId: string, userId: string) => {
      await MainApi.delete(
        `${BASE_URL}/transporters/${transporterId}/users/${userId}`
      )
    },

    getTransporterPendingPermits: async (): Promise<any> => {
      const response = await MainApi.get(
        `${BASE_URL}/transporters/pending-permits`
      )
      return response
    },

    bulkVerifyTransporters: async (
      transporterIds: number[],
      applyPendingPermit: boolean
    ): Promise<any> => {
      const { data } = await MainApi.post(
        `${BASE_URL}/transporters/pending-permits`,
        {
          applyPendingTransporterPermits: [
            {
              transporterIds,
              applyPendingPermit
            }
          ]
        }
      )
      return data
    }
  },
  /*
  Users
   */
  users: {
    getUserByEmail: async (email: string): Promise<any> => {
      const response = await MainApi.get(`${BASE_URL}/users/email/${email}`)
      return response
    }
  },

  /*
  Drivers
   */
  drivers: {
    getAllDrivers: async (data: Object): Promise<any> => {
      const response = await MainApi.get(`${BASE_URL}/drivers`, data)
      return response
    },

    getDriver: async (id: string): Promise<any> => {
      const response = await MainApi.get(`${BASE_URL}/drivers/${id}`)
      return response
    },

    getLoggedInDriver: async (activeFleetsOnly?: boolean): Promise<any> => {
      let url = `${BASE_URL}/drivers/loggedIn`
      if (activeFleetsOnly) {
        url += "?activeFleetsOnly=true"
      }
      const response = await MainApi.get(url)
      return response
    },

    createDriver: async (data: Object): Promise<any> => {
      const response = await MainApi.post(`${BASE_URL}/drivers`, data)
      return response
    },

    updateDriver: async (id: string | number, data: Object): Promise<any> => {
      const response = await MainApi.patch(`${BASE_URL}/drivers/${id}`, data)
      return response
    },

    updateLoggedInDriver: async (data: Object): Promise<any> => {
      const response = await MainApi.patch(`${BASE_URL}/drivers`, data)
      return response
    },

    getDriverLogs: async (id: string): Promise<any> => {
      const response = await MainApi.get(`${BASE_URL}/drivers/${id}/logs`)
      return response
    },

    addFleetToDriver: async (driverId: string, fleetId: string) => {
      await MainApi.post(`${BASE_URL}/drivers/${driverId}/fleets/${fleetId}`)
    },

    setFleetActiveDates: async (
      driverFleetId: string,
      data: Object
    ): Promise<any> => {
      const response = await MainApi.put(
        `${BASE_URL}/drivers/fleetDrivers/${driverFleetId}`,
        data
      )
      return response
    },

    addDriverAsset: async (id: string, data: Object): Promise<any> => {
      const response = await MainApi.post(
        `${BASE_URL}/drivers/${id}/assets`,
        data
      )
      return response
    },

    // Delete image asset from driver
    removeDriverAsset: async (id: string): Promise<any> => {
      const response = await MainApi.delete(`${BASE_URL}/drivers/${id}/assets`)
      return response
    },

    sendSMSToDriver: async (id: string): Promise<any> => {
      const response = await MainApi.post(
        `${BASE_URL}/drivers/${id}/verifyPhoneNumber`
      )
      return response
    },

    reverifyPhoneNumber: async (): Promise<any> => {
      const { data } = await MainApi.post(
        `${BASE_URL}/drivers/reverifyPhoneNumber`
      )
      return data
    },

    bulkSendSMSToDrivers: async (driverIds: number[]): Promise<any> => {
      // return new Promise((resolve) =>
      //   setTimeout(() => resolve(true), 3000)
      // )
      const { data } = await MainApi.post(
        `${BASE_URL}/drivers/verifyPhoneNumbers`,
        driverIds
      )
      return data
    },

    bulkVerifyDrivers: async (
      driverIds: number[],
      permitCheckCorrect: boolean
    ): Promise<any> => {
      const { data } = await MainApi.post(
        `${BASE_URL}/drivers/pending-permits`,
        {
          driverIds,
          permitCheckCorrect
        }
      )
      return data
    },

    addDriverToSystemUser: async (
      verificationToken: string,
      emailAddress: string
    ): Promise<any> => {
      const response = await MainApi.post(
        `${BASE_URL}/drivers/verifyPhoneNumber/addDriverToSystemUser`,
        {
          verificationToken,
          emailAddress
        }
      )
      return response
    },

    createSystemUser: async (
      verificationToken: string,
      emailAddress: string,
      password: string
    ): Promise<any> => {
      const response = await MainApi.post(
        `${BASE_URL}/drivers/verifyPhoneNumber/createSystemUserForDriver`,
        {
          verificationToken,
          emailAddress,
          password
        }
      )
      return response
    },
    getDriverPhoneNumberByToken: async (token: string): Promise<any> => {
      const response = await MainApi.get(
        `${BASE_URL}/drivers/verifyPhoneNumber/${token}`
      )
      return response
    },
    verifyPhoneNumber: async (
      verificationToken: string,
      licenseId: string
    ): Promise<any> => {
      const response = await MainApi.post(
        `${BASE_URL}/drivers/verifyPhoneNumber`,
        {
          verificationToken,
          licenseId
        }
      )
      return response
    },
    getListOfCourses: async (id: string): Promise<any> => {
      const response = await MainApi.get(
        `${BASE_URL}/tictac/view-user-course/${id}`
      )
      return response
    },

    getSSOForDriver: async (): Promise<any> => {
      const response = await MainApi.get(`${BASE_URL}/drivers/tictac/sso`)
      return response
    },

    exportCSV: async ({ config }: { config: Object }): Promise<any> => {
      const { data } = await MainApi.get(`${BASE_URL}/drivers/export`, config)
      return data
    },

    getPhoneNumberVerificationToken: async (driverId: string): Promise<any> => {
      const response = await MainApi.get(
        `${BASE_URL}/drivers/${driverId}/phoneNumberVerificationToken`
      )
      return response
    },

    getLoggedInDriverAverageRating: async (): Promise<any> => {
      const { data } = await MainApi.get(`${BASE_URL}/drivers/average`)
      return data
    },

    getLoggedInDriverTripDistribution: async (): Promise<any> => {
      const { data } = await MainApi.get(
        `${BASE_URL}/drivers/trip-distribution`
      )
      return data
    },
    getDriverByLicensId: async (
      countryCode: string,
      licenseId: string
    ): Promise<any> => {
      const { data } = await MainApi.get(
        `${BASE_URL}/drivers/countryCode/${countryCode}/licenseId/${licenseId}`
      )
      return data
    },
    getDriverPendingPermits: async (): Promise<any> => {
      const response = await MainApi.get(`${BASE_URL}/drivers/pending-permits`)
      return response
    }
  },

  /*
  News
   */
  news: {
    getAllNews: async (data: Object): Promise<any> => {
      const {
        data: { news }
      } = await MainApi.get(`${BASE_URL}/news`, data)
      return news
    },
    getTransporterNews: async (data: Object): Promise<any> => {
      const {
        data: { news }
      } = await MainApi.get(`${BASE_URL}/news`, data)
      return news
    },
    getDriverNews: async (data: Object): Promise<any> => {
      const {
        data: { news }
      } = await MainApi.get(`${BASE_URL}/news`, data)
      return news
    },
    getNews: async (id: string): Promise<any> => {
      const { data } = await MainApi.get(`${BASE_URL}/news/${id}`)
      return data
    },
    saveNews: async (data: Object): Promise<any> => {
      const response = await MainApi.put(`${BASE_URL}/news`, data)
      return response
    },
    deleteNews: async (id: string): Promise<any> => {
      const response = await MainApi.delete(`${BASE_URL}/news/${id}`)
      return response
    },
    uploadAsset: async (data: Object): Promise<any> => {
      const { data: assetId } = await MainApi.post(
        `${BASE_URL}/news/asset`,
        data
      )
      return assetId
    }
  },

  /*
  Organizations
   */
  organizations: {
    checkOrganizationNr: async (
      fleetId: string,
      orgNr: string
    ): Promise<any> => {
      const { data } = await MainApi.get(
        `${BASE_URL}/organizations/existing?fleetId=${fleetId}&orgNo=${orgNr}`
      )
      return data
    }
  },

  /*
  Ratings
   */
  ratings: {
    getAll: async ({
      config
    }: {
      config: Object
    }): Promise<{ ratings: empty, totalCount: empty, ... }> => {
      const {
        data: { ratings, totalCount }
      } = await MainApi.get(`${BASE_URL}/ratings`, config)
      return { ratings, totalCount }
    },
    getOverview: async ({ config }: { config: Object }): Promise<any> => {
      const { data } = await MainApi.get(`${BASE_URL}/ratings/overview`, config)
      return data
    },
    getStatistics: async ({ config }: { config: Object }): Promise<any> => {
      const { data } = await MainApi.get(
        `${BASE_URL}/ratings/driver/statistics`,
        config
      )
      return data
    },
    getDriverTopList: async ({ config }: { config: Object }): Promise<any> => {
      const { data } = await MainApi.get(
        `${BASE_URL}/ratings/driver-toplist`,
        config
      )
      return data
    },
    getDriverAverageRating: async ({
      driverId,
      config
    }: {
      driverId: string,
      config: Object
    }): Promise<any> => {
      const { data } = await MainApi.get(
        `${BASE_URL}/ratings/driver/${driverId}`,
        config
      )
      return data
    },

    getDriverTripDistribution: async ({
      config
    }: {
      config: Object
    }): Promise<any> => {
      const { data } = await MainApi.get(
        `${BASE_URL}/ratings/driver/trip-distribution`,
        config
      )
      return data
    },

    getHistoryForDriver: async ({
      config
    }: {
      config: Object
    }): Promise<any> => {
      const { data } = await MainApi.get(
        `${BASE_URL}/ratings/driver/history`,
        config
      )
      return data
    },
    downloadRatingCSV: async ({ config }: { config: Object }): Promise<any> => {
      const { data } = await MainApi.get(`${BASE_URL}/ratings/export`, config)
      return data
    },
    downloadDriverCSV: async ({ config }: { config: Object }): Promise<any> => {
      const { data } = await MainApi.get(
        `${BASE_URL}/ratings/export-drivertoplist`,
        config
      )
      return data
    },
    getCompanyNames: async (): Promise<any> => {
      const { data } = await MainApi.get(`${BASE_URL}/ratings/company-names`)
      return data
    },
    getFleets: async (): Promise<any> => {
      const { data } = await MainApi.get(`${BASE_URL}/ratings/fleets`)
      return data
    }
  },

  /*
  Transporter Signup
   */
  signupEntries: {
    getTransporterApplications: async ({
      params
    }: {
      params: Object
    }): Promise<any> => {
      const query = `?${qs.stringify(params)}`
      const { data } = await MainApi.get(
        `${BASE_URL}/transporter-signup/signup-entries/${query}`
      )
      return data
    },

    editTransporterApplication: async ({
      config
    }: {
      config: Object
    }): Promise<any> => {
      const { data } = await MainApi.patch(
        `${BASE_URL}/transporter-signup/signup-entries/update`,
        config
      )
      return data
    },

    getTransporterApplication: async ({
      signupEntryId
    }: {
      signupEntryId: string
    }): Promise<any> => {
      const { data } = await MainApi.get(
        `${BASE_URL}/transporter-signup/signup-entries/${signupEntryId}`
      )
      return data
    },

    approveTransporterApplication: async ({
      signupEntryId = "",
      fleetId,
      transporterNumber,
      shouldApprove = false
    }: ApproveSignupProps): Promise<any> => {
      const payload: ApproveSignupProps = {
        signupEntryId,
        shouldApprove
      }

      if (shouldApprove) {
        if (!fleetId) {
          throw "fleetId missing"
        }

        if (!transporterNumber) {
          throw "Transporter number missing"
        }

        payload.fleetId = fleetId
        payload.transporterNumber = transporterNumber
      }

      const { data } = await MainApi.post(
        `${BASE_URL}/transporter-signup/signup-entries/approve`,
        payload
      )
      return data
    }
  },

  /*
  Driver Signup
   */
  driverSignup: {
    getDriverApplications: async ({
      params
    }: {
      params: Object
    }): Promise<any> => {
      const query = `?${qs.stringify(params)}`
      const { data } = await MainApi.get(
        `${BASE_URL}/driver-signup/signup-entries/${query}`
      )
      return data
    },
    getDriverApplication: async ({
      signupEntryId
    }: {
      signupEntryId: string
    }): Promise<any> => {
      const { data } = await MainApi.get(
        `${BASE_URL}/driver-signup/signup-entries/${signupEntryId}`
      )
      return data
    },
    approveDriverApplication: async ({
      signupEntryId = "",
      fleetId,
      licenseId,
      shouldApprove = false
    }: ApproveSignupProps): Promise<any> => {
      const payload: ApproveSignupProps = {
        signupEntryId,
        shouldApprove
      }

      if (shouldApprove) {
        if (!fleetId) {
          throw "fleetId missing"
        }

        if (!licenseId) {
          throw "License Id number missing"
        }

        payload.fleetId = fleetId
        payload.licenseId = licenseId
      }

      const { data } = await MainApi.post(
        `${BASE_URL}/driver-signup/signup-entries/approve`,
        payload
      )
      return data
    },
    rejectDriverApplication: async ({
      signupEntryId = ""
    }: ApproveSignupProps): Promise<any> => {
      const {
        data
      } = await MainApi.post(
        `${BASE_URL}/driver-signup/signup-entries/reject`,
        { signupEntryId: signupEntryId }
      )
      return data
    }
  }
}
